import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePremium } from '../contexts/PremiumContext';

// Components
import CategorySelection from '../components/CreateTest/CategorySelection';
import QuestionAnswering from '../components/CreateTest/QuestionAnswering';
import TestPreview from '../components/CreateTest/TestPreview';
import UpgradePrompt from '../components/UpgradePrompt';
import LoginPrompt from '../components/LoginPrompt';
import StitchHeader from '../components/StitchHeader';

// Styles
import '../styles/CreateTest.css';

function CreateTest() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { isPremium } = usePremium();

  // 🟢 Flag برای جلوگیری از fetch مجدد
  const isRestoring = useRef(false);

  // State
  const [step, setStep] = useState('category');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [wantsDeepTest, setWantsDeepTest] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Prompts
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState('');

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost/backend';

  const categories = [
  { 
    id: 'friend', 
    name: 'دوست', 
    emoji: '👥', 
    description: 'دوستات چقدر میشناسنت',
    color: '#667eea'
  },
  { 
    id: 'newFriend', 
    name: 'دوست جدید', 
    emoji: '🤝', 
    description: 'آشنایی‌های تازه',
    color: '#10b981'
  },
  { 
    id: 'family', 
    name: 'خانواده', 
    emoji: '👨‍👩‍👧', 
    description: 'خانواده‌ات چقدر میشناسنت',
    color: '#f59e0b'
  },
  { 
    id: 'colleague', 
    name: 'همکار', 
    emoji: '💼', 
    description: 'همکارات چقدر میشناسنت',
    color: '#8b5cf6'
  },
  { 
    id: 'spouse', 
    name: 'همسر/نامزد', 
    emoji: '❤️', 
    description: 'برای عشق زندگیت', 
    isPremium: true,
    color: '#ec4899'
  },
  { 
    id: 'crush', 
    name: 'کراش', 
    emoji: '💘', 
    description: 'ببین چقدر علاقه‌مندیت میشناستت', 
    isPremium: true,
    color: '#f472b6'
  },
  { 
    id: 'classmate', 
    name: 'هم‌کلاسی', 
    emoji: '📚', 
    description: 'هم‌کلاسیات چقدر میشناسنت', 
    isPremium: true,
    color: '#06b6d4'
  },
  { 
    id: 'follower', 
    name: 'فالوور', 
    emoji: '📱', 
    description: 'فالوورات چقدر میشناسنت', 
    isPremium: true,
    color: '#a855f7'
  }
];

  // 🟢 بازیابی تست از localStorage (اولویت اول)
  useEffect(() => {
    const pendingTest = localStorage.getItem('pending_test');
    
    if (pendingTest && isAuthenticated) {
      console.log('📦 Found pending test, restoring...');
      
      try {
        const data = JSON.parse(pendingTest);
        
        // 🟢 فعال کردن flag
        isRestoring.current = true;
        
        setSelectedCategory(data.category);
        setQuestions(data.questions);
        setAnswers(data.answers);
        setCurrentQuestionIndex(data.currentIndex || 0);
        setWantsDeepTest(data.wantsDeepTest || false);
        
        const answeredCount = Object.keys(data.answers).length;
        const totalQuestions = data.questions.length;
        
        console.log(`Answered: ${answeredCount}/${totalQuestions}`);
        
        if (answeredCount === totalQuestions) {
          console.log('➡️ Going to preview');
          setStep('preview');
        } else {
          console.log('➡️ Going to answering');
          setStep('answering');
        }
        
        localStorage.removeItem('pending_test');
        console.log('✅ Test restored successfully');
        
        // 🟢 غیرفعال کردن flag بعد از 100ms
        setTimeout(() => {
          isRestoring.current = false;
        }, 100);
        
        setTimeout(() => {
          alert('خوش اومدی! می‌تونی ادامه بدی 🚀');
        }, 500);
      } catch (err) {
        console.error('❌ Error restoring test:', err);
        localStorage.removeItem('pending_test');
        isRestoring.current = false;
      }
    }
  }, [isAuthenticated]);

  // 🟢 Fetch سوالات (فقط اگه داریم از اول شروع می‌کنیم)
  useEffect(() => {
    // 🟢 چک کردن flag
    if (selectedCategory && !isRestoring.current && questions.length === 0) {
      console.log('🔄 Fetching questions for category:', selectedCategory);
      fetchQuestions(selectedCategory, 10);
    }
  }, [selectedCategory]);

  // Fetch Questions
  const fetchQuestions = async (category, count) => {
    setLoading(true);
    setError('');
    
    try {
      const url = `${apiUrl}/api/questions/list.php?type=${category}`;
      console.log('🔍 Fetching:', url);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      console.log('📦 Response:', data);

      if (data.success) {
        if (data.questions.length === 0) {
          setError(`هنوز سوالی برای دسته "${categories.find(c => c.id === category)?.name}" وجود نداره!`);
          setStep('category');
          return;
        }

        const shuffled = data.questions.sort(() => 0.5 - Math.random());
        const selected = shuffled.slice(0, Math.min(count, data.questions.length));
        
        console.log(`✅ Selected ${selected.length} questions`);
        
        setQuestions(selected);
        setCurrentQuestionIndex(0);
        setAnswers({});
        setStep('answering');
      } else {
        setError(data.error || 'خطا در دریافت سوالات');
      }
    } catch (err) {
      console.error('❌ Fetch Error:', err);
      setError('خطا در ارتباط با سرور');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCategory = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    
    if (category.isPremium && !isPremium) {
      setUpgradeFeature('test_romantic');
      setShowUpgradePrompt(true);
      return;
    }
    
    setSelectedCategory(categoryId);
  };

  const handleSelectAnswer = (optionValue) => {
    const currentQuestion = questions[currentQuestionIndex];
    
    const newAnswers = {
      ...answers,
      [currentQuestion.id]: optionValue
    };
    setAnswers(newAnswers);

    setTimeout(() => {
      if (currentQuestionIndex < questions.length - 1) {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
      } else {
        if (!wantsDeepTest && questions.length === 10) {
          setUpgradeFeature('test_deep');
          setShowUpgradePrompt(true);
        } else {
          setStep('preview');
        }
      }
    }, 300);
  };

  const goToPrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleUpgradeToDeep = () => {
    if (!isPremium) {
      setUpgradeFeature('test_deep');
      setShowUpgradePrompt(true);
      return;
    }
    
    setWantsDeepTest(true);
    setShowUpgradePrompt(false);
    fetchQuestions(selectedCategory, 30);
  };

  const handleSkipDeep = () => {
    setShowUpgradePrompt(false);
    setStep('preview');
  };

  const handleEditAnswers = () => {
    setCurrentQuestionIndex(questions.length - 1);
    setStep('answering');
  };

  const handlePublish = () => {
    console.log('🚀 Publishing test...');
    console.log('isAuthenticated:', isAuthenticated);
    console.log('selectedCategory:', selectedCategory);
    console.log('questions count:', questions.length);
    console.log('answers count:', Object.keys(answers).length);
    
    if (!isAuthenticated) {
      const pendingData = {
        category: selectedCategory,
        questions: questions,
        answers: answers,
        currentIndex: currentQuestionIndex,
        wantsDeepTest: wantsDeepTest
      };
      
      console.log('💾 Saving to localStorage:', pendingData);
      localStorage.setItem('pending_test', JSON.stringify(pendingData));
      
      setShowLoginPrompt(true);
    } else {
      createTest();
    }
  };

  const createTest = async () => {
  console.log('📤 Creating test...');
  
  // Validation
  if (!selectedCategory) {
    setError('لطفاً یک دسته انتخاب کنید');
    return;
  }

  if (questions.length === 0) {
    setError('سوالات یافت نشد!');
    return;
  }

  if (Object.keys(answers).length !== questions.length) {
    setError('لطفاً به همه سوالات پاسخ دهید');
    return;
  }

  setLoading(true);
  setError('');

  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      setError('لطفاً ابتدا وارد شوید');
      setShowLoginPrompt(true);
      return;
    }

    const testData = {
      type: selectedCategory,
      questions: questions.map(q => ({
        question_id: q.id,
        correct_value: answers[q.id]
      }))
    };

    console.log('📤 Sending:', testData);

    const response = await fetch(`${apiUrl}/api/tests/create.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(testData)
    });

    const data = await response.json();
    console.log('📥 Response:', data);

    if (data.success) {
      localStorage.removeItem('pending_test');
      alert('🎉 تست با موفقیت ساخته شد!');
      navigate(`/test/${data.test_id}`);
    } else {
      // 🟢 چک کردن محدودیت تعداد تست
      if (data.error && data.error.includes('محدودیت تعداد تست')) {
        setLoading(false);
        setUpgradeFeature('test_limit');
        setShowUpgradePrompt(true);
        return;
      }
      
      setError(data.error || 'خطا در ساخت تست');
    }
  } catch (err) {
    console.error('❌ Create test error:', err);
    setError('خطا در ارتباط با سرور');
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="create-test-container">
      <StitchHeader />
      <div className="create-test-header">
        <button className="btn-back" onClick={() => navigate('/dashboard')}>
          ← بازگشت
        </button>
        <h1>ساخت تست جدید</h1>
        {user && (
          <div className="user-badge">
            {isPremium && <span className="premium-star">⭐</span>}
            {user.name}
          </div>
        )}
      </div>

      {error && (
        <div className="error-banner">⚠️ {error}</div>
      )}

      {step === 'category' && (
        <CategorySelection
          categories={categories}
          onSelectCategory={handleSelectCategory}
          isPremium={isPremium}
        />
      )}

      {step === 'answering' && questions.length > 0 && (
        <QuestionAnswering
          question={questions[currentQuestionIndex]}
          currentIndex={currentQuestionIndex}
          totalQuestions={questions.length}
          onSelectAnswer={handleSelectAnswer}
          onPrevious={goToPrevious}
          canGoPrevious={currentQuestionIndex > 0}
        />
      )}

      {step === 'preview' && (
        <TestPreview
          questions={questions}
          answers={answers}
          categoryName={categories.find(c => c.id === selectedCategory)?.name}
          onEdit={handleEditAnswers}
          onPublish={handlePublish}
          isLoading={loading}
        />
      )}

      {showUpgradePrompt && upgradeFeature === 'test_deep' && (
        <div className="upgrade-deep-overlay">
          <div className="upgrade-deep-modal">
            <button className="modal-close" onClick={handleSkipDeep}>✕</button>
            
            <div className="upgrade-icon">🚀</div>
            <h2>تست عمیق‌تر می‌خوای؟</h2>
            <p>با 30 سوال بیشتر دوستات رو بشناس!</p>

            <div className="upgrade-benefits">
              <div className="benefit">✅ 20 سوال بیشتر</div>
              <div className="benefit">✅ نتایج دقیق‌تر</div>
              <div className="benefit">✅ تحلیل کامل‌تر</div>
            </div>

            {isPremium ? (
              <button className="btn-upgrade" onClick={handleUpgradeToDeep}>
                بله! تست عمیق‌تر 🎯
              </button>
            ) : (
              <button className="btn-upgrade" onClick={() => {
                setShowUpgradePrompt(false);
                navigate('/premium');
              }}>
                ارتقا به پریمیوم 🌟
              </button>
            )}

            <button className="btn-skip" onClick={handleSkipDeep}>
              نه، همین کافیه
            </button>
          </div>
        </div>
      )}

      <UpgradePrompt
        feature={upgradeFeature}
        isOpen={showUpgradePrompt && upgradeFeature !== 'test_deep'}
        onClose={() => setShowUpgradePrompt(false)}
      />

      <LoginPrompt
        title="برای انتشار تست باید وارد شی!"
        description="تستت آماده‌ست! فقط یه لاگین سریع می‌خواد"
        returnTo="/create"
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
      />

      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>لطفاً صبر کن...</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreateTest;
